import React from "react";
import ReactDOM from "react-dom";
import mapboxgl from "mapbox-gl";
import { ArrowIcon, WarningIcon } from "../icons";
import { useFlightPopup } from "../hooks/useFlightPopup";
import PassengerDetailPopup from "./PassengerDetailPopup";
import '../style/flight.less';

export interface PopupPassenger {
  name: string; // Properly cased full name
  group: string; // Properly cased group name
  tripId: string;
  pfhId: number;
  recordLocator?: string; // Properly cased record locator
  currentFlightHasIssue: boolean;
  futureFlightHasIssue: boolean;
  index?: number; // For UI selection tracking
}
interface PopupFlightInfo {
  type: 'flight' | 'airport';
  flightNumber: string;
  dep: string; // Departure airport code
  arr: string; // Arrival airport code
  passengers?: PopupPassenger[];
  tripCount: number;
  isCurrentFlightIssue: boolean;
  isFutureFlightIssue: boolean;
  processedFlightHistoryId?: string | number; // For airport hover
  recordLocator?: string; // For airport type
  group?: string; // For backward compatibility
}
interface FlightPopupProps {
  map: mapboxgl.Map;
  lngLat: mapboxgl.LngLatLike;
  info: PopupFlightInfo;
  popupShouldBeOnLeft?: boolean;
  isExpanded?: boolean;
  onClose: () => void;
}

const FlightPopup: React.FC<FlightPopupProps> = ({
  map,
  lngLat,
  info,
  popupShouldBeOnLeft = false,
  isExpanded = false,
  onClose,
}) => {
  const {
    hoverPosition,
    detailPopupPosition,
    selectedPassenger,
    transformedTrip,
    loading,
    passengerCardWidth,
    handlePassengerClick,
    popupRef,
    detailRef,
    passengerCardsRef,
  } = useFlightPopup({
    map,
    lngLat,
    info,
    popupShouldBeOnLeft,
    isExpanded,
    onClose,
  });

  if (!info) return null;

  return ReactDOM.createPortal(
    <>
      {/* --- Main Flight Info Popup --- */}
      <div
        ref={popupRef}
        className={
          info.type === 'airport'
            ? 'locationContainer'
            : `hoverContainer ${info.isCurrentFlightIssue ? 'withIssue' : 'withoutIssue'}`
        }
        style={{
          position: "fixed",
          left: `${hoverPosition?.x || 0}px`,
          top: `${hoverPosition?.y || 0}px`,
          zIndex: 9999,
          pointerEvents: "auto",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          maxHeight: "248px",
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <div className={
          info.type === 'airport'
            ? 'locationHeader'
            : `hoverHeader ${info.isCurrentFlightIssue ? 'withIssue' : 'withoutIssue'}`
        }>
          <div className="headerText">
            <span>{info.type === 'airport' ? info.flightNumber?.toUpperCase() : info.flightNumber}</span>
            {/* Warning icon if future issue */}
            {info.isFutureFlightIssue && (
              <span
                className="warning-icon"
                title="Future flight issue"
                dangerouslySetInnerHTML={{ __html: WarningIcon("#FFD700") }}
              />
            )}
            <span className="textView">({info.tripCount} Trips)</span>
          </div>
        </div>

        {/* Content */}
        <div className={info.type === 'airport' ? 'locationContent' : 'hoverContent'}>
          <div className="contentText">
            {info.type === 'airport' ? (
              <>
              </>
            ) : (
              <>
                <span>{info.dep}</span>
                <span className="hoverArrow">
                  <ArrowIcon />
                </span>
                <span>{info.arr}</span>
              </>
            )}
          </div>

          {/* Passengers */}
          <div className="passenger-parent scrollContent">
            {Array.isArray(info.passengers) &&
              info.passengers.map((p, idx) => (
                <div
                  key={`${p.tripId}-${p.pfhId}-${idx}`}
                  ref={(el) => {
                    if (passengerCardsRef.current) {
                      passengerCardsRef.current[idx] = el;
                    }
                  }}
                  onClick={() => handlePassengerClick({ ...p, index: idx })}
                  className={[
                    "passenger-card cursor-pointer",
                    selectedPassenger?.index === idx ? "passenger-selected" : "",
                    info.passengers?.length && info.passengers?.length > 1 ? "multiplePassenger" : ""
                  ].join(" ")}
                  style={{
                    width: info.passengers?.length && info.passengers?.length > 1 ? `${passengerCardWidth}px` : undefined
                  }}
                >
                  <div className="passengerBox">
                    <div className="passengerContainer">
                      <div className="passengerLabel">Passenger(s)</div>
                      <div className="passengerValue">
                        <span>{p.name}</span>
                        {/* Show warning icon if passenger has future flight issue */}
                        {p.futureFlightHasIssue && (
                          <span
                            className="warning-icon"
                            title="Future flight issue"
                            dangerouslySetInnerHTML={{ __html: WarningIcon("#FFD700") }}
                          />
                        )}
                      </div>
                    </div>
                    <div className="passengerContainer">
                      <div className="passengerLabel">Group</div>
                      <div className="passengerValue">{p.group}</div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* --- Passenger Detail Popup --- */}
      <PassengerDetailPopup
        selectedPassenger={selectedPassenger}
        transformedTrip={transformedTrip}
        loading={loading}
        info={info}
        detailPopupPosition={detailPopupPosition}
        detailRef={detailRef}
        isExpanded={isExpanded}
      />
    </>,
    document.body
  );
};

export default FlightPopup;
